/**
 * Centralized unDraw illustration registry — single source of truth.
 * See docs/DESIGN_SYSTEM.md and ENGINEERING.md Rule 2.
 */

export type IllustrationEntry = {
  file: string;
  alt: string;
};

export const ILLUSTRATION_NAMESPACES = [
  'page', 'hero', 'feature', 'card', 'empty', 'error', 'dashboard', 'admin',
  'results', 'jobs', 'college', 'academy', 'vault', 'support',
  'profile', 'settings', 'notification', 'onboarding', 'pricing', 'faq',
] as const;

export const ILLUSTRATION_REGISTRY: Record<string, IllustrationEntry> = {
  // —— page.* ——
  'page.home': { file: 'student-learning.svg', alt: 'Your exam journey, organized' },
  'page.results': { file: 'exam-results.svg', alt: 'Examination results' },
  'page.vault': { file: 'document-vault.svg', alt: 'Student document vault' },
  'page.admit-cards': { file: 'online-exam.svg', alt: 'Admit cards and hall tickets' },
  'page.auth': { file: 'sign-in.svg', alt: 'Sign in to your account' },
  'page.dashboard': { file: 'dashboard-overview.svg', alt: 'Your exam command center' },
  'page.notifications': { file: 'notifications-updates.svg', alt: 'Exam notifications' },
  'page.settings': { file: 'organized-content.svg', alt: 'Settings and preferences' },
  'page.jobs': { file: 'student-learning.svg', alt: 'Jobs and career opportunities' },
  'page.college': { file: 'student-learning.svg', alt: 'College search and admissions' },

  // —— hero.* (eager-loaded) ——
  'hero.home': { file: 'student-learning.svg', alt: 'Your exam journey, organized' },
  'hero.results': { file: 'exam-results.svg', alt: 'Examination results' },
  'hero.vault': { file: 'document-vault.svg', alt: 'Student document vault' },
  'hero.admit-cards': { file: 'online-exam.svg', alt: 'Admit cards and hall tickets' },
  'hero.dashboard': { file: 'dashboard-overview.svg', alt: 'Your exam command center' },
  'hero.notifications': { file: 'notifications-updates.svg', alt: 'Updates for saved exams' },
  'hero.timeline': { file: 'exam-timeline.svg', alt: 'Exam journey timeline' },
  'hero.jobs': { file: 'student-learning.svg', alt: 'Vacancies and career opportunities' },

  // —— card.* ——
  'card.result': { file: 'exam-results.svg', alt: 'Latest exam results' },
  'card.results': { file: 'exam-results.svg', alt: 'Latest exam results' },
  'card.admit-cards': { file: 'online-exam.svg', alt: 'Admit cards and hall tickets' },
  'card.vault': { file: 'document-vault.svg', alt: 'Student document vault' },
  'card.timeline': { file: 'exam-timeline.svg', alt: 'Exam journey timeline' },
  'card.categories': { file: 'student-learning.svg', alt: 'Exam categories' },
  'card.jobs': { file: 'student-learning.svg', alt: 'Jobs and opportunities' },
  'card.mock-test': { file: 'mock-test.svg', alt: 'Mock tests and practice' },
  'card.document': { file: 'document-vault.svg', alt: 'Documents and certificates' },

  // —— feature.* ——
  'feature.results': { file: 'exam-results.svg', alt: 'Examination results' },
  'feature.admit-cards': { file: 'online-exam.svg', alt: 'Admit cards' },
  'feature.vault': { file: 'secure-files.svg', alt: 'Secure document vault' },
  'feature.timeline': { file: 'progress-tracking.svg', alt: 'Track your exam journey' },
  'feature.mock-test': { file: 'mock-test.svg', alt: 'Mock tests and practice' },
  'feature.certificate': { file: 'certificate.svg', alt: 'Certificates and documents' },
  'feature.search': { file: 'file-search.svg', alt: 'Search exam results' },

  // —— empty.* ——
  'empty.default': { file: 'no-results.svg', alt: 'Nothing here yet' },
  'empty.search': { file: 'empty-search.svg', alt: 'No search results' },
  'empty.results': { file: 'exam-results.svg', alt: 'No results found' },
  'empty.notifications': { file: 'no-notification.svg', alt: 'No notifications' },
  'empty.vault': { file: 'document-vault.svg', alt: 'No documents in vault' },
  'empty.admit-cards': { file: 'online-exam.svg', alt: 'No admit cards found' },
  'empty.opportunities': { file: 'student-learning.svg', alt: 'No opportunities found' },
  'empty.saved-exams': { file: 'student-learning.svg', alt: 'No saved exams' },
  'empty.timeline': { file: 'exam-timeline.svg', alt: 'No timeline events yet' },

  // —— error.* ——
  'error.404': { file: '404-not-found.svg', alt: 'Page not found' },
  'error.500': { file: 'maintenance.svg', alt: 'Something went wrong' },
  'error.network': { file: 'maintenance.svg', alt: 'Connection error' },

  // —— dashboard.* ——
  'dashboard.overview': { file: 'dashboard-overview.svg', alt: 'Dashboard overview' },

  // —— admin.* ——
  'admin.hero': { file: 'dashboard-overview.svg', alt: 'Admin command center' },
  'admin.kpi.default': { file: 'dashboard-overview.svg', alt: 'Platform metric' },
  'admin.kpi.users': { file: 'authentication.svg', alt: 'Registered users' },
  'admin.kpi.active': { file: 'progress-tracking.svg', alt: 'Active users online' },
  'admin.kpi.results': { file: 'exam-results.svg', alt: 'Published results' },
  'admin.kpi.opportunities': { file: 'student-learning.svg', alt: 'Open opportunities' },
  'admin.kpi.cms': { file: 'organized-content.svg', alt: 'CMS drafts' },
  'admin.kpi.tickets': { file: 'help-support.svg', alt: 'Support tickets' },
  'admin.kpi.storage': { file: 'secure-files.svg', alt: 'Storage usage' },
  'admin.kpi.ai': { file: 'file-search.svg', alt: 'AI requests' },
  'admin.kpi.revenue': { file: 'organized-content.svg', alt: 'Revenue' },
  'admin.module.users': { file: 'authentication.svg', alt: 'Manage users' },
  'admin.module.results': { file: 'exam-results.svg', alt: 'Publish results' },
  'admin.module.opportunities': { file: 'student-learning.svg', alt: 'Create opportunities' },
  'admin.module.media': { file: 'document-vault.svg', alt: 'Media library' },
  'admin.module.notifications': { file: 'notifications-updates.svg', alt: 'Send notifications' },
  'admin.module.seo': { file: 'file-search.svg', alt: 'SEO manager' },
  'admin.module.cms': { file: 'organized-content.svg', alt: 'Edit content' },
  'admin.module.support': { file: 'help-support.svg', alt: 'Support tickets' },
  'admin.panel.analytics': { file: 'progress-tracking.svg', alt: 'Analytics' },
  'admin.panel.health': { file: 'maintenance.svg', alt: 'Platform health' },
  'admin.panel.approvals': { file: 'certificate.svg', alt: 'Pending approvals' },
  'admin.panel.activity': { file: 'exam-timeline.svg', alt: 'Recent activity' },
  'admin.panel.revenue': { file: 'organized-content.svg', alt: 'Revenue and billing' },
  'admin.panel.api': { file: 'file-search.svg', alt: 'API usage' },
  'admin.panel.database': { file: 'document-vault.svg', alt: 'Database statistics' },
  'admin.panel.security': { file: 'secure-files.svg', alt: 'Security alerts' },
  'admin.panel.logs': { file: 'maintenance.svg', alt: 'System logs' },
  'admin.panel.ai': { file: 'mock-test.svg', alt: 'AI assistant' },
  'admin.panel.support': { file: 'help-support.svg', alt: 'Support overview' },
  'admin.panel.notifications': { file: 'notifications-updates.svg', alt: 'Notification center' },
  'admin.panel.audit': { file: 'secure-files.svg', alt: 'Audit log' },
  'admin.panel.schedule': { file: 'exam-timeline.svg', alt: 'Today schedule' },

  // —— vault.* (document cards) ——
  'vault.default': { file: 'document-vault.svg', alt: 'Vault document' },
  'vault.identity': { file: 'secure-files.svg', alt: 'Identity document' },
  'vault.education': { file: 'student-learning.svg', alt: 'Education document' },
  'vault.certificate': { file: 'certificate.svg', alt: 'Certificate' },
  'vault.photo': { file: 'authentication.svg', alt: 'Photo or signature' },
  'vault.exam': { file: 'online-exam.svg', alt: 'Exam document' },
  'vault.other': { file: 'organized-content.svg', alt: 'Other document' },
  'vault.stat.stored': { file: 'document-vault.svg', alt: 'Documents stored' },
  'vault.stat.expiring': { file: 'progress-tracking.svg', alt: 'Documents expiring soon' },
  'vault.stat.expired': { file: 'no-notification.svg', alt: 'Expired documents' },

  // —— support.* ——
  'support.help': { file: 'help-support.svg', alt: 'Help and support' },
  'support.contact': { file: 'help-support.svg', alt: 'Contact support' },

  // —— onboarding.* ——
  'onboarding.auth': { file: 'sign-in.svg', alt: 'Create your account' },

  // —— notification.* ——
  'notification.updates': { file: 'notifications-updates.svg', alt: 'Personalized updates' },

  // —— faq.* ——
  'faq.general': { file: 'faq.svg', alt: 'Frequently asked questions' },
};

const ASSETS_BASE = '/assets/illustrations';

export function isEagerIllustrationContext(context: string): boolean {
  return context.startsWith('hero.');
}

export function resolveIllustrationContext(context: string): IllustrationEntry | null {
  const entry = ILLUSTRATION_REGISTRY[context];
  if (!entry && process.env.NODE_ENV === 'development') {
    console.warn(`[Illustration] Unknown context "${context}". Register it in config/illustrations.ts.`);
  }
  return entry ?? null;
}

export function getIllustrationAssetUrl(file: string): string {
  return `${ASSETS_BASE}/${file}`;
}
